import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IS_SPINNER_ACTIVE } from '../../../util/interceptors';
import { Organization } from '../../organization/dto/Organization';
import { OrganizationDTO } from '../../organization/dto/OrganizationDTO';
import { RecoveryOrgDto } from '../component/all-organizations/all-organizations.component';
import { UrlService } from '../../../../environments/urlService';
import { Observable } from 'rxjs';
import { RecoveryCompanyDTO } from '../component/recovery-company/dto/CompanyWithOrgDto';
import { PlanType } from '../../organization/dto/Plan';
import { RecoveryFundAdditionalInfoDto } from '../../fund-performance/dto/RecoveryFundAdditionalInfoDto';
import { BulkServcIndustriesDTO } from '../component/industry/dto/BulkServcIndustriesDTO';
import { SectorCountDTO } from '../dto/SectorCountDTO';
import { IndustriesWrapper } from "../component/all-companies-table/all-companies.util";
import { AnonymizeOption } from "../component/all-organizations/modal-anonymize-org/anonymize-org.util";

/**
 * Service for handle interactions from recovery menu.
 */
@Injectable({
  providedIn: 'root',
})
export class RecoveryService {

  private readonly url = this.urlService.getFmsUrl().slice(0, -1);
  private readonly endpoint = 'recovery';

  constructor(private http: HttpClient, private urlService: UrlService) { }

  checkUserPassword(password: string): Observable<boolean> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/check-pass`;
    return this.http.post<boolean>(link, password,
      { context: new HttpContext().set(IS_SPINNER_ACTIVE, true), withCredentials: true });
  }


  updateOrganizationAccess(dto: RecoveryOrgDto, orgUUID: string): Promise<Organization> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/org-access`;
    return this.http.put<Organization>(link, dto,
      {
        params: { orgUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true, responseType: 'json',
      }).toPromise();
  }

  updateOrganizationPlan(orgUUID: string, type: PlanType): Observable<void> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/org-plan`;
    return this.http.put<void>(link, null,
      {
        params: { orgUUID, type },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  healBox(): Promise<void> {
    const { url, endpoint } = this;
    return this.http.post<void>(`${url}/${endpoint}/heal-box`, null, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true, responseType: 'json',
    }).toPromise();
  }

  setUuidsToProfiles(): Observable<void> {
    const { url } = this;
    const link = url + '/recovery/profile-uuid';
    return this.http.get<void>(link, { withCredentials: true });
  }

  copyOrg(orgUUID: string, org: Organization): Promise<OrganizationDTO> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/copy-organization`;
    return this.http.post<OrganizationDTO>(link, org,
      {
        params: { orgUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true), withCredentials: true,
      }).toPromise();
  }

  anonymizeOrg(orgUUID: string, option: AnonymizeOption): Observable<OrganizationDTO> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/anonymize-org`;
    const dto = { orgUUID, option };
    return this.http.put<OrganizationDTO>(link, dto, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  deactivateOrActivateOrg(orgUUID: string, isDeactivated: boolean): Observable<void> {
    const { url, endpoint } = this;
    const dto = { orgUUID, isDeactivated };
    const link = `${url}/${endpoint}/deactivation`;
    return this.http.put<void>(link, dto,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true), withCredentials: true,
      });
  }

  anonymizeAll(byIds: boolean): Promise<void> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/anonymize-all`;
    return this.http.put<void>(link, {},
      {
        params: { byIds },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true), withCredentials: true,
      }).toPromise();
  }

  getAllOrganizationsForRecovery(): Promise<Organization[]> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/organizations-all`;
    return this.http.get<Organization[]>(link,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true), withCredentials: true,
      }).toPromise();
  }

  deleteFullOrganizationById(orgUUID: string): Promise<void> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/full-organization/`;
    return this.http.delete<void>(link + orgUUID, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      responseType: 'json',
    }).toPromise();
  }

  bulkDeleteOrganizations(orgs: string[]): Promise<string[]> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/organization/bulk-delete`;
    return this.http.post<string[]>(link, orgs,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true), withCredentials: true,
      }).toPromise();
  }

  setBulkIndustries(data: BulkServcIndustriesDTO[]): Promise<void> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/set-industry`;
    return this.http.put<void>(link, data, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    }).toPromise();
  }

  /**
   * Getter of unique industries and sectors which are sorted in ascending lexicographic order
   */
  getUniqueSectorsAndIndustries(): Observable<IndustriesWrapper> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/sectors`;
    return this.http.get<IndustriesWrapper>(link, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getCompaniesByCategory(categories: IndustriesWrapper): Observable<RecoveryCompanyDTO[]> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/by-category`;
    return this.http.put<RecoveryCompanyDTO[]>(link, categories, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getSectorsAndCount(): Observable<SectorCountDTO[]> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/sectors/counts`;
    return this.http.get<SectorCountDTO[]>(link, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  updateFundIsUsedInStatistics(uuid: string, isUsedInStatistics: boolean): Observable<void> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/fund/use-in-statistics`;
    return this.http.put<void>(link, null, {
      params: { uuid, isUsedInStatistics },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  updateOrgIsUsedInStatistics(orgUUID: string, isUsedInStatistics: boolean): Observable<void> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/org/use-in-statistics`;
    return this.http.put<void>(link, null, {
      params: { orgUUID, isUsedInStatistics },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getAdditionalFundData(): Observable<RecoveryFundAdditionalInfoDto[]> {
    const { url, endpoint } = this;
    const link = `${url}/${endpoint}/fund/additional-info`;
    return this.http.get<RecoveryFundAdditionalInfoDto[]>(link, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }
}
